import appConfig from '@/features/app/constants/app-config';
import captureSentryException from '@/features/app/functions/captureSentryException';
import staleQueryIfNotError from '@/features/app/functions/staleQueryIfNotError';
import CustomTrans from '@/shared/components/custom-trans';
import useAuthenticatedQuery from '@/shared/hooks/useAuthenticatedQuery';
import useAxios from '@/shared/hooks/useAxios';
import { css, cx } from '@emotion/css';
import { Button, breakpoints } from '@thisisdmg/tirecloud-patterns';
import { App, AutoComplete, Grid, Input } from 'antd';
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import classes from './search.module.css';
import { debounce } from 'lodash-es';
import useFeatureFlags from '@/features/feature-flags/hooks/useFeatureFlags';

function Search({ isMobileSearch = false }: { isMobileSearch?: boolean }) {
  const { t } = useTranslation();
  const featureFlags = useFeatureFlags();
  const [searchQuery] = useQueryParam('q', StringParam);
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);
  const navigate = useNavigate();
  const [, setSearchHistory] = useState<string[]>([]);
  const [suggestionDropdownOpen, setSuggestionDropdownOpen] = useState(false);
  const breakpoint = Grid.useBreakpoint();

  // const [autocompleteOptions, setAutoCompleteOptions] = useState<
  //   Array<{
  //     value: string;
  //   }>
  // >([]);
  const axios = useAxios();
  const { notification } = App.useApp();

  const { data: matchCodes, isError } = useAuthenticatedQuery<string[]>({
    queryKey: ['matchCodes'],
    queryFn: async () => {
      return axios
        .get(`/api/v1/tire-sizes/match-codes`)
        .then((res) => res.data)
        .then((res) => {
          return res.matchCodes;
        })
        .catch((error) => {
          captureSentryException(error);
          return Promise.reject(error);
        });
    },
    ...staleQueryIfNotError(appConfig.productDataStaleTime),
    enabled: featureFlags.component.productSearch.enableSuggestion,
  });

  useEffect(() => {
    if (isError) {
      notification.error({
        key: 'APP004',
        message: <CustomTrans i18nKey={'component.search.matchCodeFetchError'} />,
        duration: 0,
      });
    }
  }, [isError, notification]);

  useEffect(() => {
    if (!searchQuery) {
      setSearchKeyword(null);
    }
  }, [searchQuery]);

  // const updateSuggestions = useCallback(
  //   (value: string) => {
  //     if (!matchCodes) return;
  //     let suggestions: typeof autocompleteOptions = [];

  //     if (value.length >= 1) {
  //       const filterData = matchCodes.filter((item) => {
  //         return (
  //           item.toLowerCase().includes(value.toLowerCase()) ||
  //           item
  //             .replace(/[A-Z]/g, '')
  //             .toLowerCase()
  //             .includes(value.replace(/[A-Z]/g, '').toLowerCase())
  //         );
  //       });
  //       const sortData = filterData;
  //       const match = sortData;

  //       const filtered = sortData.filter((item, index) => {
  //         return !match.includes(item, index + 1);
  //       });
  //       suggestions = filtered.map((item) => {
  //         return {
  //           value: item,
  //         };
  //       });
  //     }
  //     // setAutoCompleteOptions(suggestions.slice(0, 5));
  //   },
  //   [matchCodes]
  // );

  const autocompleteOptions = useMemo(() => {
    if (!matchCodes) return [];

    const value = searchKeyword ?? '';
    let suggestions: Array<{
      value: string;
    }> = [];

    if (value.length >= 1) {
      const filterData = matchCodes.filter((item) => {
        return (
          item.toLowerCase().includes(value.toLowerCase()) ||
          item
            .replace(/[A-Z]/g, '')
            .toLowerCase()
            .includes(value.replace(/[A-Z]/g, '').toLowerCase())
        );
      });
      const sortData = filterData;
      const match = sortData;

      const filtered = sortData.filter((item, index) => {
        return !match.includes(item, index + 1);
      });
      suggestions = filtered.map((item) => {
        return {
          value: item,
        };
      });
    }

    return suggestions;
  }, [matchCodes, searchKeyword]);

  const applySearch = useCallback(
    (searchStr: string) => {
      navigate({
        pathname: `/products`,
        search: new URLSearchParams({
          ...(searchStr.length > 0 ? { q: searchStr } : {}),
        }).toString(),
      });
      setSearchHistory((prev) => {
        const newHistory = prev.filter((item) => item !== searchStr);
        newHistory.unshift(searchStr);
        return newHistory;
      });
    },
    [navigate]
  );

  useEffect(() => {
    const debouncedApplySearch = debounce((searchStr: string) => applySearch(searchStr), 500);
    if (searchKeyword !== null) {
      debouncedApplySearch(searchKeyword);
    }
    return () => debouncedApplySearch.cancel();
  }, [searchKeyword, applySearch]);

  const location = useLocation();

  const autocompOption = useMemo(() => {
    return [
      {
        label: t('component.search.suggestions'),
        options:
          autocompleteOptions.length > 0
            ? autocompleteOptions
            : [{ value: t('component.search.noSuggestion'), disabled: true }],
      },
    ];
  }, [
    t,
    autocompleteOptions,
    // searchHistory
  ]);

  const mobileSearchClassName = useMemo(() => {
    if (location.pathname.startsWith('/brand') || location.pathname === '/about') {
      return 'tw:hidden';
    }
    return undefined;
  }, [location.pathname]);

  const ref: ComponentProps<typeof AutoComplete>['ref'] = useRef(null);
  const preventAutoSelectRef = useRef(false);

  return (
    <div
      className={
        isMobileSearch ? mobileSearchClassName : 'tw:h-[60px] tw:max-w-[400px] tw:!flex-grow'
      }
      {...(isMobileSearch ? { id: 'main-mobile-searchbox' } : { id: 'main-header-searchbox' })}
    >
      <AutoComplete
        ref={ref}
        className={cx(
          'tw:w-full mobile-search-root tw:max-sm:pb-0 tw:sm:p-0',
          classes.mobile_search_root
        )}
        options={featureFlags.component.productSearch.enableSuggestion ? autocompOption : []}
        onSearch={
          featureFlags.component.productSearch.enableSuggestion
            ? (value) => {
                setSearchKeyword(value);
              }
            : undefined
        }
        onInputKeyDown={(e) => {
          if (e.key === 'Enter') {
            preventAutoSelectRef.current = true;
          }
        }}
        onSelect={
          featureFlags.component.productSearch.enableSuggestion
            ? (searchStr) => {
                if (preventAutoSelectRef.current) {
                  preventAutoSelectRef.current = false;
                  return;
                }
                setSearchKeyword(searchStr);
                applySearch(searchStr);
                setSuggestionDropdownOpen(false);
                ref.current?.nativeElement.querySelector('input')?.blur();
                if (window._paq) {
                  window._paq.push(['trackEvent', 'Search', 'Click Search Suggestion', searchStr]);
                }
              }
            : undefined
        }
        classNames={{
          popup: {
            root: 'search-dropdown',
          },
        }}
        defaultValue={searchKeyword === null ? (searchQuery ?? '') : searchKeyword}
        listHeight={130}
        open={suggestionDropdownOpen}
        onOpenChange={(visible) => setSuggestionDropdownOpen(visible)}
        defaultActiveFirstOption={false}
      >
        <div className={isMobileSearch ? 'mobile-search-sm' : 'top-search-box'}>
          <div
            className={css`
              padding: var(--spacing-4);
              border-radius: 200px;
              border: 1px solid var(--shade-200);
              color: var(--content-text-color);
              background-color: var(--white-color);
              padding-right: 56px;

              @media (min-width: ${breakpoints.sm}px) {
                border-color: var(--shade-800);
                background-color: var(--shade-900);
              }

              & > input {
                padding: 0 0 0 4px !important;
                border: none !important;
              }

              & > input:focus,
              & > input:hover {
                box-shadow: none !important;
              }

              [dir='rtl'] & {
                padding-left: 56px;
                padding-right: var(--spacing-4);
              }
            `}
            onClick={(e) => {
              // move the cursor to the end of the input
              const target = e.target;
              if (!(target instanceof HTMLElement)) return;

              if (target instanceof HTMLInputElement) {
                // if the input is not focused, focus it
                if (document.activeElement !== target || !breakpoint.sm) {
                  target.focus();
                  target.setSelectionRange(target.value.length, target.value.length);
                }
              } else {
                const input = target.querySelector('input');

                // if the input is not focused, focus it
                if (document.activeElement !== input) {
                  input?.focus();
                  input?.setSelectionRange(input.value.length, input.value.length);
                }
              }
            }}
          >
            <Input
              type="search"
              placeholder={t('component.search.placeholder')}
              rootClassName={isMobileSearch ? undefined : 'tw:!placeholder-(--shade-200)'}
              value={searchKeyword === null ? (searchQuery ?? '') : searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onPressEnter={() => {
                applySearch(searchKeyword ?? '');
                setSuggestionDropdownOpen(false);
                ref.current?.nativeElement.querySelector('input')?.blur();
              }}
              data-test-id="header-search-input"
            />
          </div>
          <Button
            type="submit"
            disabled={!searchKeyword}
            className={cx(
              'btn-icon submit-search',
              (searchKeyword?.length ?? 0) > 0 ? 'tw:!text-(--t-blue)' : undefined,
              css`
                &:active {
                  transform: translateY(-50%) !important;
                }
              `
            )}
            onClick={() => {
              applySearch(searchKeyword ?? '');
              setSuggestionDropdownOpen(false);
              ref.current?.nativeElement.querySelector('input')?.blur();
            }}
          >
            {t('component.search.searchBtn')}
          </Button>
        </div>
      </AutoComplete>
    </div>
  );
}

export default Search;
